import {
  Box,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { MenuItem as MenuItemModel } from "services/facultyPortalClient";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import { useQuery } from "react-query";

const getStyles = (theme: Theme): { [key: string]: SxProps } => ({
  userInfo: {
    p: 1.5,
    mt: "-8px",
    mb: "8px",
    textAlign: "center",
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.gray.main,
  },
  user: {
    fontWeight: "bold",
    fontSize: "18px",
  },
  membership: {
    pt: 1,
    fontSize: "14px",
  },
  menu: {
    color: theme.palette.primary.dark,
  },
});

type UserMenuProps = {
  anchor: HTMLElement | null;
  handleClose: () => void;
};

export default function UserMenu({ anchor, handleClose }: UserMenuProps) {
  const styles = getStyles(useTheme());
  const { menuService } = useGlobalContext();

  const { data: menu, isFetching } = useQuery(
    ["getTopMenu"],
    () => menuService.getTopMenu(),
    {
      staleTime: Infinity,
      enabled: !!anchor,
    }
  );

  const menuItems = menu?.menuItems;
  const { userInfo } = menu ?? {};

  const isExternal = (url: string) => {
    return !url.startsWith("/");
  };

  const onMenuItemClick = (menuItem: MenuItemModel) => {
    if (isExternal(menuItem.url)) {
      window.open(menuItem.url, "_blank");
    } else {
      window.location.href = menuItem.url;
    }
  };

  return (
    <Menu
      anchorEl={anchor}
      id="account-menu"
      open={!!anchor}
      onClose={handleClose}
      onClick={handleClose}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {isFetching && (
        <Box sx={styles.userInfo}>
          <Skeleton width={100} />
          <Skeleton width={150} />
        </Box>
      )}
      {isFetching && (
        <MenuItem dense sx={styles.menu}>
          <Skeleton width={150} />
        </MenuItem>
      )}

      {!isFetching && !!userInfo && (
        <Box sx={styles.userInfo}>
          <Box sx={styles.user}>
            {userInfo?.firstName} {userInfo?.lastName}
          </Box>
        </Box>
      )}

      {!isFetching &&
        menuItems?.map((mi) => (
          <MenuItem
            dense
            sx={styles.menu}
            key={mi.name}
            onClick={() => onMenuItemClick(mi)}
          >
            {!!mi.icon && (
              <ListItemIcon sx={styles.menu}>
                <i className={mi.icon} />
              </ListItemIcon>
            )}
            <Box flexGrow={1}>{mi.name}</Box>
            {isExternal(mi.url) && (
              <OpenInNewIcon fontSize="small" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
    </Menu>
  );
}
